import { useState } from 'react';
import {
  Button,
  Group,
  IconEdit,
  IconPlusCircle,
  IconTrash,
  Overlay,
  Table,
  Token,
  Typography,
  useModalPortal
} from '@screentone/core';
import { useQueryClient } from '@tanstack/react-query';
import { JsonData, JsonEditor } from 'json-edit-react';

import { ConfirmationDialog, ResponsiveLoader } from 'components';
import ContentTypeToken from 'components/content-type-token/ContentTypeToken';
import { useAlert } from 'contexts/alert/useAlert';
import { PublicationSettingsQuery, QueryConfig, useDeleteQueryConfigMutation } from 'data/generated/graphql';
import QueryConfigForm from './BaseQueryConfigForm';
import styles from './UnifiedQueryConfigTable.module.scss';

interface StaticQueryRow {
  key: string;
  label: string;
  jsonField: {
    stringValue: string;
    error: string;
    handleChange: (value: string) => void;
  };
}

interface Props {
  publicationKey: string;
  baseQueryConfigs: QueryConfig[];
  isLoading: boolean;
  staticRows: StaticQueryRow[];
  onSaveStaticRow: (key: string) => Promise<void>;
  isReadOnly?: boolean;
}

const parseJson = (val: string): Record<string, unknown> | null => {
  try {
    return JSON.parse(val) as Record<string, unknown>;
  } catch {
    return null;
  }
};

const UnifiedQueryConfigTable = ({
  publicationKey,
  baseQueryConfigs,
  isLoading,
  staticRows,
  onSaveStaticRow,
  isReadOnly = false
}: Props) => {
  const { alertError, alertSuccess } = useAlert();
  const queryClient = useQueryClient();
  const { renderNode } = useModalPortal();

  const [editing, setEditing] = useState<QueryConfig | null | 'new'>(null);
  const [pendingDelete, setPendingDelete] = useState<QueryConfig | null>(null);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [editingStaticKey, setEditingStaticKey] = useState<string | null>(null);

  const { mutate: deleteMutation, isLoading: isDeleting } = useDeleteQueryConfigMutation({
    onSuccess: (_response, variables) => {
      const deletedId = variables.input.id;
      queryClient.setQueriesData<PublicationSettingsQuery>({ queryKey: ['PublicationSettings'] }, (old) => {
        if (!old?.publicationSetting) return old;
        return {
          ...old,
          publicationSetting: {
            ...old.publicationSetting,
            baseQueryConfigs: ((old.publicationSetting.baseQueryConfigs ?? []) as QueryConfig[]).filter(
              (qc) => qc.id !== deletedId
            )
          }
        };
      });
    }
  });

  const handleDeleteConfirm = () => {
    if (!pendingDelete) return;
    const target = pendingDelete;
    deleteMutation(
      { input: { publicationKey, id: target.id } },
      {
        onSuccess: () => {
          alertSuccess(`Query "${target.name}" deleted`);
          setPendingDelete(null);
        },
        onError: (err: unknown) => {
          alertError(err instanceof Error ? err.message : 'Failed to delete query');
        }
      }
    );
  };

  const handleSaveStatic = async (key: string) => {
    setSavingKey(key);
    try {
      await onSaveStaticRow(key);
      alertSuccess('Query saved');
      setEditingStaticKey(null);
    } catch (err) {
      alertError(err instanceof Error ? err.message : 'Failed to save query');
    } finally {
      setSavingKey(null);
    }
  };

  const editingStaticRow = editingStaticKey ? staticRows.find((r) => r.key === editingStaticKey) ?? null : null;

  return (
    <>
      {editing && (
        <Overlay
          onDismiss={() => setEditing(null)}
          status="open"
          renderNode={renderNode}
          className={styles.overlayScrollable}
        >
          <QueryConfigForm existing={editing === 'new' ? undefined : editing} onDone={() => setEditing(null)} />
        </Overlay>
      )}

      {editingStaticRow && (
        <Overlay
          onDismiss={() => setEditingStaticKey(null)}
          status="open"
          renderNode={renderNode}
          className={styles.overlay}
        >
          <div className={styles.overlayBody}>
            <Group gap="md" direction="column">
              <Typography variant="header3" margin={{ all: 'none' }}>
                {editingStaticRow.label}
              </Typography>
              <JsonEditor
                data={parseJson(editingStaticRow.jsonField.stringValue) ?? {}}
                setData={(val: JsonData) => editingStaticRow.jsonField.handleChange(JSON.stringify(val, null, 2))}
                showIconTooltips
                showCollectionCount="when-closed"
                indent={4}
                maxWidth="100%"
                restrictDrag
              />
            </Group>
          </div>
          <div className={styles.overlayFooter}>
            <Group gap="xs">
              <Button secondary onClick={() => setEditingStaticKey(null)}>
                Cancel
              </Button>
              <Button
                primary
                onClick={() => void handleSaveStatic(editingStaticRow.key)}
                disabled={savingKey === editingStaticRow.key || !!editingStaticRow.jsonField.error}
              >
                {savingKey === editingStaticRow.key ? 'Saving…' : 'Save'}
              </Button>
            </Group>
          </div>
        </Overlay>
      )}

      {!isReadOnly && (
        <Group align="end" margin={{ bottom: 'sm' }}>
          <Button tertiary icon={IconPlusCircle} onClick={() => setEditing('new')}>
            New Query
          </Button>
        </Group>
      )}

      {isLoading ? (
        <ResponsiveLoader />
      ) : (
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.Cell isLabel>Icon</Table.Cell>
              <Table.Cell isLabel>Name</Table.Cell>
              <Table.Cell isLabel>Content Type Label</Table.Cell>
              <Table.Cell isLabel>Category</Table.Cell>
              <Table.Cell isLabel>Status</Table.Cell>
              {!isReadOnly && <Table.Cell isLabel>Actions</Table.Cell>}
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {baseQueryConfigs.map((qc) => (
              <Table.Row key={qc.id}>
                <Table.Cell>
                  <ContentTypeToken
                    type={qc.name}
                    margin={{}}
                    iconSvgPath={(qc.iconSvgPath as string | undefined) ?? undefined}
                  />
                </Table.Cell>
                <Table.Cell>{qc.name}</Table.Cell>
                <Table.Cell>{qc.contentTypeLabel ?? '—'}</Table.Cell>
                <Table.Cell>
                  {qc.category ? String(qc.category).charAt(0).toUpperCase() + String(qc.category).slice(1) : '—'}
                </Table.Cell>
                <Table.Cell>
                  <Token color={qc.enabled ? 'emerald' : 'gray'}>{qc.enabled ? 'Enabled' : 'Disabled'}</Token>
                </Table.Cell>
                <Table.Cell>
                  {!isReadOnly && (
                    <Group gap="xs">
                      <Button size="sm" tertiary onClick={() => setEditing(qc)} icon={IconEdit} />
                      <Button
                        size="sm"
                        tertiary
                        icon={IconTrash}
                        color="lava"
                        onClick={() => setPendingDelete(qc)}
                        disabled={isDeleting}
                      />
                    </Group>
                  )}
                </Table.Cell>
              </Table.Row>
            ))}

            {staticRows.map(({ key, label, jsonField }) => (
              <Table.Row key={key}>
                <Table.Cell>—</Table.Cell>
                <Table.Cell>
                  <Typography variant="bodytext" margin={{ all: 'none' }}>
                    {label}
                  </Typography>
                </Table.Cell>
                <Table.Cell colSpan="2">
                  {jsonField.stringValue.trim() ? (
                    <Token color="emerald">Set</Token>
                  ) : (
                    <Token color="lava">Not set</Token>
                  )}
                </Table.Cell>
                <Table.Cell>
                  <Token color="gray">Static</Token>
                </Table.Cell>
                <Table.Cell>
                  {!isReadOnly && (
                    <Button size="sm" tertiary icon={IconEdit} onClick={() => setEditingStaticKey(key)} />
                  )}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      {pendingDelete && (
        <ConfirmationDialog
          title="Delete Query"
          description={`Are you sure you want to delete query "${pendingDelete.name}"?`}
          isLoading={isDeleting}
          onClickFunc={handleDeleteConfirm}
          showDialog={(show) => {
            if (!show) setPendingDelete(null);
          }}
        />
      )}
    </>
  );
};

export default UnifiedQueryConfigTable;
